import React, { useEffect, useRef, useState, useCallback } from "react";
import { Table } from "antd";
import type { RowProps, TableProps } from "antd";
import { ExpandableConfig, SortOrder } from "antd/es/table/interface";
import styled, { css } from "styled-components";
import { calcRem } from "styles/theme";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import AntdPagination, {
  PaginationChangePage,
} from "components/blocks/pagination/AntdPagination";
import { COLORS, LIGHT_THEME_COLORS } from "styles/colors";
import * as mediaQuery from "components/MediaQuery";
import { FLEX } from "styles/flex";
import Spinner from "../loader/Spinner";
import NoData from "components/templates/nopage/NoData";
import { useNoticeHeight } from "components/NoticePovider";
import { useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { toTrim } from "utils/functions";

type PaginationProps = {
  queryInfo?: BasePagination;
  onChangePage?: PaginationChangePage;
};

export interface ExpandTableProps extends TableProps<any> {
  tableWrapperHeight?: number;
}

const TableSpinner = () => {
  return (
    <TableSpinnerWrapper>
      <Spinner />
    </TableSpinnerWrapper>
  );
};

export const onSortAntd = (order: SORT_DIRECTION) => {
  if (order === SORT_DIRECTION.ASC) {
    return SORT_DIRECTION.DESC;
  } else return SORT_DIRECTION.ASC;
};

export const changeSortOrderString = (order?: SortOrder) => {
  switch (order) {
    case "ascend":
      return SORT_DIRECTION.ASC;
    case "descend":
      return SORT_DIRECTION.DESC;
    default:
      return SORT_DIRECTION.ASC;
  }
};

export const antdExpandable = (props?: ExpandableConfig<any>) => {
  if (!props) return undefined;
  return {
    ...props,
    expandIcon: ({
      expanded,
      onExpand,
      record,
      expandable,
    }: {
      expanded: boolean;
      onExpand: (record: any, event: React.MouseEvent<HTMLElement>) => void;
      record: any;
      expandable: boolean;
    }) =>
      expandable ? (
        <ExpandButton
          onClick={(e: React.MouseEvent<HTMLElement>) => onExpand(record, e)}
          data-testid="atoms-table-table-expand-button-expand"
        >
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
        </ExpandButton>
      ) : undefined,
  };
};

export const changeSortOrderAntd = (
  queryInfo: BasePagination,
  key: React.Key
) => {
  if (queryInfo.sortType === (key as string)) {
    switch (queryInfo.sortDirection) {
      case SORT_DIRECTION.ASC:
        return "ascend";
      case SORT_DIRECTION.DESC:
        return "descend";
      default:
        return "ascend";
    }
  } else return null;
};

const TableNoData = (props: { noImage?: boolean }): JSX.Element => {
  if (props.noImage) {
    return (
      <TableNoDataWrapper>
        <div className="no-data-text"> There is no data to display</div>
      </TableNoDataWrapper>
    );
  }
  return (
    <TableNoDataWrapper>
      <NoData />
    </TableNoDataWrapper>
  );
};

export const DashboardCardDetailTable = (
  props: TableProps<any>
): JSX.Element => (
  <DashboardCardDetailTableWrapper>
    <Table
      pagination={false}
      bordered={false}
      locale={{
        emptyText: "",
      }}
      {...props}
    />
  </DashboardCardDetailTableWrapper>
);

export const DashboardChartDetailTable = (
  props: TableProps<any> & PaginationProps
): JSX.Element => {
  const isMobile = mediaQuery.useIsMobile();
  return (
    <DashboardChartTableWrapper>
      <div className="table-wrapper">
        <Table
          showSorterTooltip
          bordered={false}
          pagination={false}
          sticky={true}
          {...(props as TableProps<any>)}
        />
      </div>
      {props.queryInfo !== undefined && props.onChangePage !== undefined && (
        <div className="pagination-wrapper">
          <AntdPagination
            rowsPerPage={props.queryInfo.pageLimit}
            rowCount={props.queryInfo.total}
            onChangePage={props.onChangePage}
            currentPage={props.queryInfo.pageNum}
            pageRangeDisplayed={isMobile ? 2 : 5}
          />
        </div>
      )}
    </DashboardChartTableWrapper>
  );
};

export const CommonTable = (
  props: TableProps<any> & PaginationProps
): JSX.Element => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const paginationWrapperRef = useRef<HTMLDivElement>(null);
  const { noticeHeight } = useNoticeHeight();
  const [tableWrapperHeight, setTableWrapperHeight] = useState<number>(0);
  const location = useLocation();
  const isMobile = mediaQuery.useIsMobile();

  const getTableWrapperHeight = useCallback(() => {
    if (tableWrapperRef.current) {
      setTableWrapperHeight(
        window.innerHeight -
          tableWrapperRef.current.getBoundingClientRect().top -
          document.documentElement.scrollTop -
          (document.getElementById("inner-container")?.scrollTop || 0) -
          (paginationWrapperRef.current?.clientHeight || 0) -
          noticeHeight
      );
    }
  }, [noticeHeight, location.pathname, location.search, props.dataSource]);

  useEffect(() => {
    getTableWrapperHeight();
    window.addEventListener("resize", getTableWrapperHeight);
    return () => {
      window.removeEventListener("resize", getTableWrapperHeight);
    };
  }, [getTableWrapperHeight]);

  const Row = (props: RowProps) => {
    const style: React.CSSProperties = {
      ...props.style,
      height: 52,
      cursor: props.className?.includes("noPointer") ? "default" : "pointer",
    };

    return <CommonTableTr {...props} style={style} />;
  };

  return (
    <CommonTableWrapper
      ref={tableWrapperRef}
      minHeight={tableWrapperHeight}
      isRowLink={props.onRow !== undefined}
    >
      <div className="table-wrapper">
        <Table
          // bordered={false}
          showSorterTooltip={false}
          pagination={false}
          components={{
            body: {
              row: Row,
            },
          }}
          locale={{
            emptyText: props.loading ? (
              <div style={{ height: "200px" }} />
            ) : (
              <TableNoData />
            ),
          }}
          scroll={{ x: isMobile ? "100%" : undefined }}
          {...(props as TableProps<any>)}
          expandable={antdExpandable(props.expandable)}
          loading={{
            spinning: (props.loading as boolean) || false,
            indicator: <TableSpinner />,
          }}
          tableLayout={isMobile ? "auto" : "fixed"} // column width 변동되는 점 방지
          rowClassName={`${props.rowClassName} antd-table-row-mydw`}
        />
      </div>
      {props.queryInfo !== undefined && props.onChangePage !== undefined && (
        <div className="pagination-wrapper" ref={paginationWrapperRef}>
          <AntdPagination
            rowsPerPage={props.queryInfo.pageLimit}
            rowCount={props.queryInfo.total}
            onChangePage={props.onChangePage}
            currentPage={props.queryInfo.pageNum}
          />
        </div>
      )}
    </CommonTableWrapper>
  );
};

export const ExpandTable = (props: ExpandTableProps): JSX.Element => {
  const isMobile = mediaQuery.useIsMobile();
  const Row = (props: RowProps) => {
    const style: React.CSSProperties = {
      ...props.style,
      height: 30,
    };

    return <CommonTableTr {...props} style={style} />;
  };
  const [hoveredRowKey, setHoveredRowKey] = useState<number | null>(null);

  // onRow 이벤트 부모와 합치기
  const localOnRow = (record: any, rowIndex?: number) => {
    // 자식 컴포넌트 내의 로직
    const localHandlers = {
      onMouseEnter: () => {
        if (rowIndex !== undefined && rowIndex !== null)
          setHoveredRowKey(rowIndex); // 마우스가 올라갔을 때 상태 업데이트
      },
      onMouseLeave: () => {
        setHoveredRowKey(null); // 마우스가 떠났을 때 상태 초기화
      },
      className: rowIndex === hoveredRowKey ? "hovered-row" : "", // 상태에 따라 클래스 적용
    };

    // 부모 컴포넌트로부터 받은 onRow 핸들러와 결합
    const parentHandlers = props.onRow ? props.onRow(record, rowIndex) : {};

    const className = [
      toTrim(parentHandlers.className),
      toTrim(localHandlers.className),
    ]
      .filter(Boolean)
      .join(" ");

    // 두 핸들러를 병합
    return {
      ...parentHandlers,
      ...localHandlers,
      onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
        if (parentHandlers.onMouseEnter) parentHandlers.onMouseEnter(event);
        localHandlers.onMouseEnter();
      },
      onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
        if (parentHandlers.onMouseLeave) parentHandlers.onMouseLeave(event);
        localHandlers.onMouseLeave();
      },
      className,
    };
  };
  return (
    <ExpandTableWrapper minHeight={props.tableWrapperHeight}>
      <Table
        // bordered={false}
        showSorterTooltip={false}
        pagination={false}
        components={{
          body: {
            row: Row,
          },
        }}
        scroll={{ x: isMobile ? "100%" : undefined }}
        tableLayout={"fixed"}
        {...(props as TableProps<any>)}
        expandable={antdExpandable(props.expandable)}
        locale={{
          emptyText: props.loading ? "" : <TableNoData />,
        }}
        onRow={localOnRow}
      />
    </ExpandTableWrapper>
  );
};

export const ReportTable = (
  props: TableProps<any> & PaginationProps & { tableWrapperHeight?: number }
): JSX.Element => {
  const [hoveredRowKey, setHoveredRowKey] = useState<number | null>(null);
  const isMobile = mediaQuery.useIsMobile();

  const Row = (props: RowProps) => {
    const style: React.CSSProperties = {
      ...props.style,
    };

    return <CommonTableTr {...props} style={style} />;
  };

  // onRow 이벤트 부모와 합치기
  const localOnRow = (record: any, rowIndex?: number) => {
    // 자식 컴포넌트 내의 로직
    const localHandlers = {
      onMouseEnter: () => {
        if (rowIndex !== undefined && rowIndex !== null)
          setHoveredRowKey(rowIndex); // 마우스가 올라갔을 때 상태 업데이트
      },
      onMouseLeave: () => {
        setHoveredRowKey(null); // 마우스가 떠났을 때 상태 초기화
      },
      className: rowIndex === hoveredRowKey ? "hovered-row" : "", // 상태에 따라 클래스 적용
    };

    // 부모 컴포넌트로부터 받은 onRow 핸들러와 결합
    const parentHandlers = props.onRow ? props.onRow(record, rowIndex) : {};

    const className = [
      toTrim(parentHandlers.className),
      toTrim(localHandlers.className),
    ]
      .filter(Boolean)
      .join(" ");
    // 두 핸들러를 병합
    return {
      ...parentHandlers,
      ...localHandlers,
      onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
        if (parentHandlers.onMouseEnter) parentHandlers.onMouseEnter(event);
        localHandlers.onMouseEnter();
      },
      onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
        if (parentHandlers.onMouseLeave) parentHandlers.onMouseLeave(event);
        localHandlers.onMouseLeave();
      },
      className,
    };
  };

  return (
    <ReportTableWrapper minHeight={props.tableWrapperHeight}>
      <div className="table-wrapper">
        <Table
          // bordered={false}
          showSorterTooltip={false}
          pagination={false}
          components={{
            body: {
              row: Row,
            },
          }}
          scroll={{ x: isMobile ? "100%" : undefined }}
          tableLayout={isMobile ? "auto" : "fixed"}
          {...(props as TableProps<any>)}
          onRow={localOnRow}
          loading={{
            spinning: (props.loading as boolean) || false,
            indicator: <TableSpinner />,
          }}
          locale={{
            emptyText: props.loading ? "" : <TableNoData />,
          }}
        />
      </div>
      {props.queryInfo !== undefined && props.onChangePage !== undefined && (
        <div className="pagination-wrapper">
          <AntdPagination
            rowsPerPage={props.queryInfo.pageLimit}
            rowCount={props.queryInfo.total}
            onChangePage={props.onChangePage}
            currentPage={props.queryInfo.pageNum}
          />
        </div>
      )}
    </ReportTableWrapper>
  );
};

export const AlertViewTable = (
  props: TableProps<any> & { tableWrapperHeight?: number }
): JSX.Element => {
  return (
    <AlertViewTableWrapper minHeight={props.tableWrapperHeight}>
      <Table
        showSorterTooltip={false}
        pagination={false}
        scroll={{
          x: mediaQuery.isMobile ? "100%" : undefined,
          y: 250,
        }}
        rowKey="key"
        loading={{
          spinning: (props.loading as boolean) || false,
          indicator: <TableSpinner />,
        }}
        locale={{
          emptyText: props.loading ? "" : <TableNoData noImage />,
        }}
        tableLayout={mediaQuery.isMobile ? "auto" : "fixed"}
        {...(props as TableProps<any>)}
      />
    </AlertViewTableWrapper>
  );
};

export const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  .ant-table-wrapper {
    width: 100%;
    height: 100%;
  }
  .ant-table-wrapper .ant-table-thead th.ant-table-column-has-sorters:hover {
    background-color: ${({ theme }) =>
      theme.value === "light"
        ? LIGHT_THEME_COLORS.LIGHTGRAY
        : COLORS.PRIMARY3} !important;
  }
  .ant-table-wrapper .ant-table-column-sorter-up,
  .ant-table-wrapper .ant-table-column-sorter-down {
    font-size: ${calcRem(11)};
  }
  .ant-table-wrapper
    .ant-table-column-sorter
    .ant-table-column-sorter-up
    + .ant-table-column-sorter-down {
    margin-top: -0.4em;
  }
  .ant-table-wrapper .ant-table-column-sorters .ant-table-column-sorter {
    margin-inline-start: 0;
  }
  .ant-table-wrapper
    .ant-table-ping-left:not(.ant-table-has-fix-left)
    .ant-table-container::before {
    box-shadow: inset 10px 0 8px -8px rgba(${({ theme }) => (theme.value === "light" ? "255, 255, 255" : "27, 35, 50")}, 0.94);
  }
  .ant-table-wrapper
    .ant-table-ping-right:not(.ant-table-has-fix-right)
    .ant-table-container::after {
    box-shadow: inset -10px 0 8px -8px
      rgba(
        ${({ theme }) =>
          theme.value === "light" ? "255, 255, 255" : "27, 35, 50"},
        0.94
      );
  }
  .table-wrapper
    .ant-table-ping-left:not(.ant-table-has-fix-left)
    .ant-table-container::before {
    box-shadow: inset 10px 0 8px -8px rgba(${({ theme }) => (theme.value === "light" ? "255, 255, 255" : "11, 23, 36")}, 0.94);
  }
  .table-wrapper
    .ant-table-ping-right:not(.ant-table-has-fix-right)
    .ant-table-container::after {
    box-shadow: inset -10px 0 8px -8px
      rgba(
        ${({ theme }) =>
          theme.value === "light" ? "255, 255, 255" : "11, 23, 36"},
        0.94
      );
  }
  .ant-table {
    font-size: ${calcRem(13.5)};
    line-height: ${calcRem(16)};
    color: ${({ theme }) => theme.COLORS.WHITE};
    scrollbar-color: ${({ theme }) =>
      theme.value === "light"
        ? `${COLORS.LIGHTGRAY} ${LIGHT_THEME_COLORS.LIGHTGRAY}`
        : "#545E77 #273457"};
  }
  .ant-empty-normal {
    margin-block: 0;
  }

  .ant-table-filter-dropdown {
    .ant-dropdown-menu {
      padding: 0;
    }
    .ant-dropdown-menu-item {
      padding: 4px;
    }
  }

  .ant-dropdown-menu-title-content {
    color: ${COLORS.BLACK} !important;
  }

  .ant-table-filter-trigger,
  .ant-table-column-sorter {
    color: unset !important;
  }

  .ant-table-filter-trigger.active {
    color: ${({ theme }) => theme.COLORS.SELECTED} !important;
  }

  // column header 아이콘 정렬 변경

  .ant-table-column-sorters {
    cursor: pointer;
    .ant-table-column-title {
      flex: unset;
    }
    justify-content: flex-start;
    gap: 4px;
  }
  .table-header-sort-center {
    .ant-table-column-sorters {
      justify-content: center;
    }
  }
`;

const DashboardCardDetailTableWrapper = styled(Wrapper)<{ minHeight?: number }>`
  .ant-table-row,
  .ant-table-cell,
  td {
    border: none !important;
    place-content: center;
  }
  thead th,
  .total-count {
    white-space: nowrap;
    padding: 0 !important;
  }
  tfoot .total-count {
    text-align: right;
    display: block;
  }

  .ant-table-content {
    min-height: ${({ minHeight }) => minHeight}px;
  }

  .ant-table-row {
    .ant-table-cell {
      padding: 4px 2px;
    }
  }

  tfoot {
    tr > td {
      padding: 10px 2px !important;
    }
  }
`;

const DashboardChartTableWrapper = styled(Wrapper)<{ minHeight?: number }>`
  display: flex;
  flex-direction: column;

  .table-wrapper {
    flex: 1 1 auto;
  }

  .pagination-wrapper {
    flex: 0 0 auto;
  }

  .ant-table-thead th {
    padding: 4px 16px !important;
    background-color: ${({ theme }) =>
      theme.value === "light"
        ? LIGHT_THEME_COLORS.LIGHTGRAY
        : COLORS.PRIMARY3} !important;

    .ant-table-column-title {
      font-weight: 500 !important;
    }
  }
  .ant-table-cell {
    padding: 6px 16px !important;
  }
  tbody {
    tr {
      &:last-child {
        > td {
          border-bottom: none;
        }
      }
    }
  }

  .no-data {
    margin: 40px 0;
  }
`;

const CommonTableWrapper = styled(Wrapper)<{
  minHeight?: number;
  isRowLink?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  .ant-table-content {
    min-height: ${({ minHeight }) => minHeight}px;
  }

  .table-wrapper {
    flex: 1 1 auto;
  }
  .pagination-wrapper {
    flex: 0 0 auto;
    padding-bottom: 10px;
  }

  .ant-table-wrapper {
    > div {
      height: 100%;
    }
    ${({ isRowLink }) =>
      !isRowLink &&
      `
      .ant-table-cell {
        cursor: default !important;
      }
    `}

    .ant-spin-container {
      height: 100%;

      .ant-table {
        height: 100%;

        .ant-table-container {
          height: 100%;

          .ant-table-content {
            height: 100%;
          }
        }
      }
    }
  }

  .table-wrapper .ant-table-thead > tr > th,
  .table-wrapper .ant-table-thead > tr > td {
    border-bottom: 0;
    background-color: ${({ theme }) =>
      theme.value === "light" ? LIGHT_THEME_COLORS.LIGHTGRAY : COLORS.PRIMARY3};
  }
  .ant-table-thead > tr > th:first-child,
  .ant-table-thead > tr > td:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
    border-start-start-radius: 3px !important;
    padding-left: 23px !important;
    ${mediaQuery.isMobile} {
      padding-left: 7px !important;
    }
  }
  .table-wrapper .ant-table-wrapper .ant-table-expanded-row-fixed {
    margin-left: -23px;
    padding-left: 23px;
  }
  .ant-table-thead > tr > th:last-child,
  .ant-table-thead > tr > td:last-child {
    border-top-right-radius: 3px;
    border-start-end-radius: 3px !important;
    border-bottom-right-radius: 3px;
  }
  .ant-table-thead th {
    padding: 12px 4px !important;
    color: ${({ theme }) => theme.COLORS.WHITE} !important;
    padding-right: 10px !important;
    vertical-align: middle;

    &:first-child {
      padding-left: 10px !important;
    }

    .ant-table-column-title {
      font-size: ${calcRem(14)} !important;
      font-weight: 500 !important;
    }
  }
  .ant-table-wrapper .ant-table-tbody > tr {
    > td,
    .ant-table-cell {
      border-bottom-width: 0;
      border-bottom-color: transparent;
      border-top: 1px solid ${({ theme }) => theme.COLORS.LIGHTGRAYBORDER};
      transition: none;
    }
  }
  .ant-table-cell {
    padding: 14px 4px !important;
    padding-right: 10px !important;
    ${mediaQuery.isMobile} {
      padding: 10px 4px !important;
    }
  }

  .antd-table-row-mydw {
    &:hover {
      .row-click-button {
        filter: brightness(120%) saturate(120%);
      }
    }
  }

  .table-button-wrapper {
    ${FLEX.FlexEndCenter};
    gap: 10px;
    &.align-left {
      justify-content: flex-start;
    }
  }

  .ant-table-expanded-row {
    > td {
      padding: 0 !important;
    }
  }

  .default-rule-row {
    color: ${({ theme }) => theme.COLORS.SELECTED} !important;
    td {
      color: ${({ theme }) => theme.COLORS.SELECTED} !important;
    }
  }
`;

const CommonTableTr = styled.tr`
  td {
    vertical-align: middle;

    &:first-child {
      padding-left: 23px !important;
      ${mediaQuery.isMobile} {
        padding-left: 7px !important;
      }
    }
  }
  /* cursor: pointer; */

  :hover {
    background-color: ${({ theme }) =>
      theme.value === "light"
        ? `${theme.COLORS.WHITE}03`
        : `${theme.COLORS.WHITE}08`};
  }
`;

const ReportTableWrapper = styled(CommonTableWrapper)<{ minHeight?: number }>`
  .ant-table {
    font-size: ${calcRem(13)};
    line-height: ${calcRem(15)};
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
  .ant-table-thead th {
    border: none !important;
    padding: 8px 4px !important;
    color: ${({ theme }) =>
      theme.value === "light" ? COLORS.BORDER : theme.COLORS.WHITE} !important;

    .ant-table-column-title {
      font-size: ${calcRem(13)} !important;
      font-weight: 500 !important;
    }
  }
`;

const AlertViewTableWrapper = styled(Wrapper)<{ minHeight?: number }>`
  .ant-table {
    font-size: ${calcRem(12)};
    line-height: ${calcRem(15)};
  }
  .ant-table-thead th {
    padding: 5px 4px !important;
  }

  .ant-table-cell {
    padding: 5px !important;
  }
`;

const TableSpinnerWrapper = styled.div`
  width: 100%;
  text-align: center;
  margin: 50px 0;
`;

const TableNoDataWrapper = styled.div`
  padding: 30px 0;

  img {
    width: 65px;
    max-width: 65px !important;
  }

  > .no-data-text {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}90`};
  }
`;

const ExpandTableWrapper = styled(Wrapper)<{ minHeight?: number }>`
  .ant-table-content {
    min-height: ${({ minHeight }) => minHeight}px;
  }
`;

export const ExpandTableContainer = styled.div`
  ${({ theme }) =>
    css`
      .expand-collapse-icon {
        color: ${theme.COLORS.WHITE};
        ${FLEX.FlexCenterCenter};
      }

      /* Expanded row 안쪽(하위 테이블)의 expand icon을 조금 더 안쪽으로 */
      .ant-table-expanded-row .ant-table-wrapper {
        td.ant-table-row-expand-icon-cell {
          padding-left: 38px !important;
          padding-right: 0 !important;
        }
        .ant-table-expand-icon-col {
          width: 58px !important;
        }
      }
      /* .ant-table-wrapper tr:not(.ant-table-expanded-row) {
        td:last-child,
        th:last-child {
          padding-right: 15px !important;
        }
      } */
      .ant-table-wrapper tr.ant-table-expanded-row {
        > td,
        > .ant-table-cell,
        &:hover > td {
          background: none !important;
          background-color: rgba(
            0,
            0,
            0,
            ${theme.value === "light" ? 0.02 : 0.2}
          ) !important;
          td,
          .ant-table-cell {
            border-top: none;
          }
        }
        th:last-child {
          padding-right: 0 !important;
        }
      }

      .ant-table-expanded-row
        .ant-table-wrapper
        .ant-table
        tbody
        tr:not(:first-child)
        td {
        padding-top: 7px !important;
      }

      .ant-table-expanded-row
        .ant-table-wrapper
        .ant-table
        tbody
        tr:not(:last-child)
        td {
        padding-bottom: 7px !important;
      }
      .spinner-wrapper {
        padding: 0;
      }

      /* AntD 내부 측정용 row(ant-table-measure-row)에는 간격을 주지 않음 */
      .ant-table-expanded-row
        .ant-table-wrapper
        .ant-table
        tbody
        tr.ant-table-measure-row
        > td {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    `}

  .ant-table-expanded-row > td {
    padding: 0 !important;

    .table-wrapper {
      flex: auto;
    }
    .ant-table-wrapper,
    .ant-table-container,
    .ant-table-content,
    .ant-table {
      height: auto !important;
      min-height: 0 !important;
    }
  }
`;

const ExpandButton = styled.button`
  ${FLEX.FlexCenterCenter};
  width: 20px;
  height: 20px;
  font-size: ${calcRem(16)};
  color: ${({ theme }) => theme.COLORS.WHITE};
`;
